import type { MtbTrailMetadata, UpdatePlaceData } from "~/lib/api";
import { PillSelector } from "~/components/ui/pill-selector";
import { BUILD_LABELS, CHARACTER_LABELS, FEATURE_LABELS } from "./mtbTrailLabels";
import { InfoField } from "~/components/place/InfoField";
import { PanelSection } from "~/components/ui/panel-section";
import { MtbTrailStylePills } from "./MtbTrailStylePills";

interface MtbTrailStyleSectionProps {
  metadata: MtbTrailMetadata | null;
  canEdit?: boolean;
  onUpdate?: (data: UpdatePlaceData) => Promise<void>;
}

export function MtbTrailStyleSection({ metadata, canEdit, onUpdate }: MtbTrailStyleSectionProps) {
  const { character, features, build } = metadata ?? {};

  const hasCharacter = character && character.length > 0;
  const hasFeatures = features && features.length > 0;
  const hasStyle = !!(hasCharacter || hasFeatures || build);

  if (!canEdit && !hasStyle) return null;

  const updateMetadata = (updates: Partial<MtbTrailMetadata>) => {
    onUpdate?.({ metadata: { ...metadata, ...updates } });
  };

  return (
    <>
      <PanelSection.Separator />
      <PanelSection.Root>
        <PanelSection.Title>Trail Style</PanelSection.Title>
        <PanelSection.Content className="space-y-3">
          {canEdit ? (
            <>
              <InfoField label="Character">
                <PillSelector.Root
                  value={character ?? null}
                  onChange={(c) => updateMetadata({ character: c ?? undefined })}
                  label="Character"
                  multi
                >
                  {Object.entries(CHARACTER_LABELS).map(([value, label]) => (
                    <PillSelector.Option key={value} value={value}>
                      {label}
                    </PillSelector.Option>
                  ))}
                </PillSelector.Root>
              </InfoField>
              <InfoField label="Features">
                <PillSelector.Root
                  value={features ?? null}
                  onChange={(f) => updateMetadata({ features: f ?? undefined })}
                  label="Features"
                  multi
                >
                  {Object.entries(FEATURE_LABELS).map(([value, label]) => (
                    <PillSelector.Option key={value} value={value}>
                      {label}
                    </PillSelector.Option>
                  ))}
                </PillSelector.Root>
              </InfoField>
              <InfoField label="Build">
                <PillSelector.Root
                  value={build ?? null}
                  onChange={(b) => updateMetadata({ build: b ?? undefined })}
                  label="Build"
                >
                  {Object.entries(BUILD_LABELS).map(([value, label]) => (
                    <PillSelector.Option key={value} value={value}>
                      {label}
                    </PillSelector.Option>
                  ))}
                </PillSelector.Root>
              </InfoField>
            </>
          ) : metadata ? (
            <MtbTrailStylePills metadata={metadata} />
          ) : null}
        </PanelSection.Content>
      </PanelSection.Root>
    </>
  );
}
